import { jsPDF } from "jspdf"
import autoTable from "jspdf-autotable"

// ── Font Loading ─────────────────────────────────────────────────────
let fontsLoaded = false
let fontLoadPromise = null

const loadFonts = async (doc) => {
  if (fontsLoaded) {
    applyFonts(doc)
    return
  }

  if (!fontLoadPromise) {
    fontLoadPromise = (async () => {
      try {
        const [regularRes, boldRes] = await Promise.all([
          fetch("/fonts/Roboto-Regular.ttf"),
          fetch("/fonts/Roboto-Bold.ttf"),
        ])
        const [regularBuf, boldBuf] = await Promise.all([
          regularRes.arrayBuffer(),
          boldRes.arrayBuffer(),
        ])

        // Convert ArrayBuffer to base64
        const toBase64 = (buf) => {
          const bytes = new Uint8Array(buf)
          let binary = ""
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i])
          }
          return btoa(binary)
        }

        window.__robotoRegularB64 = toBase64(regularBuf)
        window.__robotoBoldB64 = toBase64(boldBuf)
        fontsLoaded = true
      } catch (err) {
        console.warn("Could not load Roboto fonts, falling back to Helvetica:", err)
        fontsLoaded = false
      }
    })()
  }

  await fontLoadPromise
  applyFonts(doc)
}

const applyFonts = (doc) => {
  if (!fontsLoaded) return
  doc.addFileToVFS("Roboto-Regular.ttf", window.__robotoRegularB64)
  doc.addFileToVFS("Roboto-Bold.ttf", window.__robotoBoldB64)
  doc.addFont("Roboto-Regular.ttf", "Roboto", "normal")
  doc.addFont("Roboto-Regular.ttf", "Roboto", "italic")
  doc.addFont("Roboto-Bold.ttf", "Roboto", "bold")
  doc.addFont("Roboto-Bold.ttf", "Roboto", "bolditalic")
  doc.setFont("Roboto")
}

// ── Category Colors (matching UI) ────────────────────────────────────
const CAT1_PDF_COLORS = {
  "Cabinet": { bg: [237, 233, 254], text: [124, 58, 237] },
  "Accessories": { bg: [219, 234, 254], text: [37, 99, 235] },
  "Special": { bg: [254, 243, 199], text: [217, 119, 6] },
  "Charges": { bg: [252, 231, 243], text: [190, 24, 93] },
}

const PARENT_CATEGORY_VALUES = [
  "Cabinet",
  "Accessories",
  "Special",
  "Charges",
]

// ── Eliton Brand Colors (PDF) ────────────────────────────────────
const ELITON_PDF = {
  red: [229, 0, 25],
  dark: [33, 33, 33],
  gray: [133, 133, 133],
  lightRed: [253, 232, 234],
  redText: [154, 0, 17],
}

// Customer-friendly section labels
const CUSTOMER_SECTION_LABELS = {
  "Wall": "ΚΡΕΜΑΣΤΑ (Πάνω)",
  "Base": "ΒΑΣΕΩΣ (Κάτω)",
  "Tall": "ΟΛΟΣΩΜΑ",
}

// ── Header Drawing ───────────────────────────────────────────────────
const drawHeader = (doc, { companyDetails, cartItemCount, pdfType, currentUser, t }) => {
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 14

  // Company info - top left
  doc.setFontSize(14)
  doc.setFont("Roboto", "bold")
  doc.setTextColor(30, 41, 59) // #1e293b
  doc.text(companyDetails?.name || "FlexFlow ERP", margin, 20)

  doc.setFontSize(8)
  doc.setFont("Roboto", "normal")
  doc.setTextColor(100, 116, 139) // #64748b

  const companyLines = []
  if (companyDetails?.vat) companyLines.push(`VAT: ${companyDetails.vat}`)
  if (companyDetails?.address) {
    let addr = companyDetails.address
    if (companyDetails.city) addr += `, ${companyDetails.city}`
    if (companyDetails.postal_code) addr += ` ${companyDetails.postal_code}`
    companyLines.push(addr)
  }
  const contactParts = []
  if (companyDetails?.phone) contactParts.push(companyDetails.phone)
  if (companyDetails?.email) contactParts.push(companyDetails.email)
  if (contactParts.length) companyLines.push(contactParts.join(" | "))

  companyLines.forEach((line, i) => {
    doc.text(line, margin, 26 + i * 4)
  })

  // Document title - top right
  const titles = {
    manufacturer: t("MANUFACTURING ORDER"),
    customer: t("ORDER CONFIRMATION"),
    supplier: t("PURCHASE ORDER"),
  }

  doc.setFontSize(12)
  doc.setFont("Roboto", "bold")
  doc.setTextColor(59, 130, 246) // #3b82f6
  doc.text(titles[pdfType] || "", pageWidth - margin, 20, { align: "right" })

  // Date and items count
  const now = new Date()
  const dateStr = now.toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric" })

  doc.setFontSize(8)
  doc.setFont("Roboto", "normal")
  doc.setTextColor(100, 116, 139)
  doc.text(`${t("Date")}: ${dateStr}`, pageWidth - margin, 26, { align: "right" })
  doc.text(`${t("Items")}: ${cartItemCount}`, pageWidth - margin, 30, { align: "right" })

  // Salesperson / User info
  if (currentUser && (currentUser.first_name || currentUser.name || currentUser.email)) {
    const userName = currentUser.first_name
      ? `${currentUser.first_name} ${currentUser.last_name || ""}`.trim()
      : currentUser.name || ""
    const userEmail = currentUser.email || ""

    doc.setFontSize(8)
    doc.setFont("Roboto", "bold")
    doc.setTextColor(30, 41, 59)
    doc.text(`${t("Salesperson")}:`, pageWidth - margin, 36, { align: "right" })
    doc.setFont("Roboto", "normal")
    doc.setTextColor(100, 116, 139)
    const userLabel = userName ? `${userName} (${userEmail})` : userEmail
    doc.text(userLabel, pageWidth - margin, 40, { align: "right" })
  }

  // Blue divider line
  const lineY = 44
  doc.setDrawColor(59, 130, 246)
  doc.setLineWidth(0.5)
  doc.line(margin, lineY, pageWidth - margin, lineY)

  return lineY + 6 // Return Y position after header
}

// ── Eliton Logo SVG → PNG Conversion ────────────────────────────
let elitonLogoCache = null

const getElitonLogoBase64 = () => {
  if (elitonLogoCache) return Promise.resolve(elitonLogoCache)
  return new Promise((resolve) => {
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="460" height="140" viewBox="0 0 114.882 34.923">
      <rect width="3.844" height="26.376" transform="translate(41.399 7.835)" fill="#212121" />
      <path d="M66.085,3.8l-.1-.584L65.7,2.681l-.438-.438L64.772,2l-.633-.1-.584.1-.536.243-.389.438-.292.535-.1.584.1.584A1.91,1.91,0,0,0,65.7,4.92l.292-.535Z" transform="translate(-20.843 -0.637)" fill="#212121" />
      <rect width="3.796" height="34.211" transform="translate(31.909)" fill="#212121" />
      <path d="M22.857,22.815q-.219-.656-.438-1.314a9.706,9.706,0,0,0-17.812,0l-.486,1.314Zm-18.979,3.8c1.478,8.576,11.592,11.457,17.13,4.964l.779-1.168h4.283a24.9,24.9,0,0,1-1.606,2.871A13.516,13.516,0,0,1,.082,23.788q.146-.8.292-1.606C4.394,7.887,24.3,8.659,26.9,23.4a18.568,18.568,0,0,1,.049,3.212H3.878" transform="translate(0 -3.982)" fill="#212121" />
      <path d="M80.378,26.133l.1.925A4.874,4.874,0,0,0,85.1,31.049l.925-.1V34.8l-1.168.049A8.77,8.77,0,0,1,76.631,27.2l-.049-1.07V0h3.8V8.273h5.645V12.02H80.378Z" transform="translate(-25.646 0)" fill="#212121" />
      <path d="M115.157,20.182l-.827-1.119a9.737,9.737,0,0,0-17.082,7.251l.243,1.363-3.552,1.508L93.6,27.579a13.52,13.52,0,0,1,24.187-10.268l.876,1.363Z" transform="translate(-31.289 -3.963)" fill="#e50019" />
      <path d="M117.6,28.946l.146,1.363a9.739,9.739,0,0,1-16.692,7.056l-.876-1.022L96.63,37.852l.973,1.314a13.509,13.509,0,0,0,23.846-10.122l-.292-1.606Z" transform="translate(-32.359 -9.189)" fill="#212121" />
      <path d="M143.668,23.266l.1-1.119a7.119,7.119,0,0,1,14.064,0l.1,1.119V38.353h3.8V23.266L161.673,22c-2.1-13.495-21.115-12.534-21.8,1.12V38.353h3.8Z" transform="translate(-46.84 -4.141)" fill="#212121" />
    </svg>`
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = 460
      canvas.height = 140
      const ctx = canvas.getContext("2d")
      ctx.drawImage(img, 0, 0, 460, 140)
      URL.revokeObjectURL(url)
      elitonLogoCache = canvas.toDataURL("image/png")
      resolve(elitonLogoCache)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
    img.src = url
  })
}

// ── Customer-Branded Header (Eliton) ────────────────────────────
const drawCustomerHeader = (doc, params) => {
  const { cartItemCount, currentUser, t, customerInfo, quoteTitle, settingsChips, profitMargin, logoBase64 } = params
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 14
  let y = 10

  // Eliton logo above the red bar
  if (logoBase64) {
    // Logo: 32mm wide, proportional height (~10mm)
    const logoW = 32
    const logoH = logoW * (34.923 / 114.882) // maintain aspect ratio
    doc.addImage(logoBase64, "PNG", margin, y, logoW, logoH)
    y += logoH + 2
  } else {
    // Fallback: text-based logo
    doc.setFontSize(18)
    doc.setFont("Roboto", "bold")
    doc.setTextColor(...ELITON_PDF.dark)
    doc.text("eliton", margin, y + 6)
    y += 10
  }

  // Red accent bar
  doc.setFillColor(...ELITON_PDF.red)
  doc.rect(margin, y, pageWidth - margin * 2, 1.5, "F")
  y += 5

  // Document title (right-aligned, next to where logo was)
  doc.setFontSize(11)
  doc.setFont("Roboto", "bold")
  doc.setTextColor(...ELITON_PDF.red)
  doc.text(t("ΠΡΟΣΦΟΡΑ ΚΟΥΖΙΝΑΣ"), pageWidth - margin, y, { align: "right" })
  y += 5

  // Date + items count (right)
  const now = new Date()
  const dateStr = now.toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric" })
  doc.setFontSize(8)
  doc.setFont("Roboto", "normal")
  doc.setTextColor(...ELITON_PDF.gray)
  const dateItemsLine = `${t("Date")}: ${dateStr}  |  ${t("Items")}: ${cartItemCount}`
  doc.text(dateItemsLine, pageWidth - margin, y, { align: "right" })

  // Quote title (left)
  if (quoteTitle) {
    doc.setFontSize(9)
    doc.setFont("Roboto", "bold")
    doc.setTextColor(...ELITON_PDF.dark)
    doc.text(quoteTitle, margin, y)
  }
  y += 4

  // Salesperson (right)
  if (currentUser && (currentUser.first_name || currentUser.name || currentUser.email)) {
    const userName = currentUser.first_name
      ? `${currentUser.first_name} ${currentUser.last_name || ""}`.trim()
      : currentUser.name || ""
    doc.setFontSize(7)
    doc.setFont("Roboto", "normal")
    doc.setTextColor(...ELITON_PDF.gray)
    const userLabel = userName ? `${t("Salesperson")}: ${userName}` : `${t("Salesperson")}: ${currentUser.email}`
    doc.text(userLabel, pageWidth - margin, y, { align: "right" })
    y += 5
  } else {
    y += 2
  }

  // ── Customer info box ──
  if (customerInfo && (customerInfo.name || customerInfo.phone || customerInfo.email)) {
    const boxX = margin
    const boxY = y
    const boxW = pageWidth - margin * 2

    const line1Parts = []
    if (customerInfo.name) line1Parts.push(customerInfo.name)
    if (customerInfo.phone) line1Parts.push(`Τηλ: ${customerInfo.phone}`)
    if (customerInfo.email) line1Parts.push(customerInfo.email)
    const line1 = line1Parts.join("  |  ")
    const line2 = customerInfo.address || ""
    const boxH = line2 ? 16 : 12

    doc.setFillColor(...ELITON_PDF.lightRed)
    doc.setDrawColor(...ELITON_PDF.red)
    doc.setLineWidth(0.3)
    doc.roundedRect(boxX, boxY, boxW, boxH, 1.5, 1.5, "FD")

    // Section label
    doc.setFontSize(6.5)
    doc.setFont("Roboto", "bold")
    doc.setTextColor(...ELITON_PDF.redText)
    doc.text(t("ΣΤΟΙΧΕΙΑ ΠΕΛΑΤΗ"), boxX + 3, boxY + 4)

    // Customer details
    doc.setFontSize(8)
    doc.setFont("Roboto", "normal")
    doc.setTextColor(...ELITON_PDF.dark)
    doc.text(line1, boxX + 3, boxY + 9)
    if (line2) {
      doc.text(line2, boxX + 3, boxY + 13)
    }

    y += boxH + 3
  }

  // ── Configuration summary ──
  if (settingsChips && settingsChips.length > 0) {
    doc.setFontSize(6.5)
    doc.setFont("Roboto", "bold")
    doc.setTextColor(...ELITON_PDF.red)
    doc.text(t("ΠΡΟΔΙΑΓΡΑΦΕΣ"), margin, y)
    y += 3.5

    doc.setFontSize(7)
    doc.setFont("Roboto", "normal")
    doc.setTextColor(71, 85, 105)

    const chipText = settingsChips.map(c => c.label).join("  •  ")
    const maxWidth = pageWidth - margin * 2
    const splitLines = doc.splitTextToSize(chipText, maxWidth)
    splitLines.forEach(line => {
      doc.text(line, margin + 1, y)
      y += 3.5
    })
    y += 1
  }

  // Red divider line
  doc.setDrawColor(...ELITON_PDF.red)
  doc.setLineWidth(0.4)
  doc.line(margin, y, pageWidth - margin, y)

  return y + 4
}

// ── Column Definitions ───────────────────────────────────────────────
const getColumns = (pdfType, t) => {
  if (pdfType === "customer") {
    return [
      { header: "#", dataKey: "idx" },
      { header: "", dataKey: "img" },
      { header: "SKU", dataKey: "sku" },
      { header: t("Product"), dataKey: "product" },
      { header: t("Dimensions"), dataKey: "dimensions" },
      { header: t("Color/Texture"), dataKey: "color" },
      { header: t("Qty"), dataKey: "qty" },
      { header: t("Unit Price"), dataKey: "unitPrice" },
      { header: t("Total"), dataKey: "total" },
    ]
  }

  const base = [
    { header: "#", dataKey: "idx" },
    { header: "SKU", dataKey: "sku" },
    { header: t("Product"), dataKey: "product" },
    { header: t("Dimensions"), dataKey: "dimensions" },
    { header: t("Color/Texture"), dataKey: "color" },
    { header: t("Qty"), dataKey: "qty" },
  ]

  if (pdfType === "manufacturer") {
    return [
      ...base,
      { header: t("B2B Price"), dataKey: "b2bPrice" },
      { header: t("Final Price"), dataKey: "finalPrice" },
      { header: t("Profit/Unit"), dataKey: "profitUnit" },
      { header: t("Total"), dataKey: "total" },
    ]
  }

  // supplier
  return [
    ...base,
    { header: t("Unit Price"), dataKey: "unitPrice" },
    { header: t("Total"), dataKey: "total" },
  ]
}

// ── Customer Section Reordering ──────────────────────────────────────
// Splits Cabinet into sub-sections and reorders:
// Wall → Worktop → Base → other kitchen → Accessories → Special → Charges
const buildCustomerSections = (groupedCart) => {
  const sections = []
  const kitchenGroups = groupedCart.groups["Cabinet"] || {}
  const kitchenColors = CAT1_PDF_COLORS["Cabinet"] || { bg: [241, 245, 249], text: [71, 85, 105] }
  const defaultColors = { bg: [241, 245, 249], text: [71, 85, 105] }

  if (kitchenGroups["Wall"]) {
    sections.push({
      label: CUSTOMER_SECTION_LABELS["Wall"] || "Wall",
      colors: kitchenColors,
      cat2Groups: { "Wall": kitchenGroups["Wall"] },
    })
  }

  // Worktop is under Charges category
  const allCharges = groupedCart.groups["Charges"] || {}
  if (allCharges["Worktop"]) {
    sections.push({
      label: "ΠΑΓΚΟΣ",
      colors: CAT1_PDF_COLORS["Charges"] || defaultColors,
      cat2Groups: { "Worktop": allCharges["Worktop"] },
    })
  }

  if (kitchenGroups["Base"]) {
    sections.push({
      label: CUSTOMER_SECTION_LABELS["Base"] || "Base",
      colors: kitchenColors,
      cat2Groups: { "Base": kitchenGroups["Base"] },
    })
  }

  Object.entries(kitchenGroups).forEach(([cat2Key, items]) => {
    if (cat2Key !== "Wall" && cat2Key !== "Base") {
      sections.push({
        label: CUSTOMER_SECTION_LABELS[cat2Key] || cat2Key,
        colors: kitchenColors,
        cat2Groups: { [cat2Key]: items },
      })
    }
  })

  if (groupedCart.groups["Accessories"]) {
    sections.push({
      label: "ΑΞΕΣΟΥΑΡ",
      colors: CAT1_PDF_COLORS["Accessories"] || defaultColors,
      cat2Groups: groupedCart.groups["Accessories"],
    })
  }

  if (groupedCart.groups["Special"]) {
    sections.push({
      label: "ΕΙΔΙΚΑ",
      colors: CAT1_PDF_COLORS["Special"] || defaultColors,
      cat2Groups: groupedCart.groups["Special"],
    })
  }

  // Remaining Charges (non-Worktop)
  const remainingCharges = {}
  for (const [k, v] of Object.entries(allCharges)) {
    if (k !== "Worktop") remainingCharges[k] = v
  }
  if (Object.keys(remainingCharges).length > 0) {
    sections.push({
      label: "ΧΡΕΩΣΕΙΣ",
      colors: CAT1_PDF_COLORS["Charges"] || defaultColors,
      cat2Groups: remainingCharges,
    })
  }

  return sections
}

// ── Build Table Body ─────────────────────────────────────────────────
const buildTableBody = (params, pdfType) => {
  const { groupedCart, calculateFinalPrice, formatCustomDimensions } = params
  const rows = []
  let itemCounter = 0
  const colCount = pdfType === "manufacturer" ? 10 : 8

  let grandTotalB2B = 0
  let grandTotalFinal = 0

  // Build section list: customer PDF uses reordered sections
  const sections = pdfType === "customer"
    ? buildCustomerSections(groupedCart)
    : groupedCart.orderedCat1Keys.map(cat1Key => ({
        label: cat1Key,
        colors: CAT1_PDF_COLORS[cat1Key] || { bg: [241, 245, 249], text: [71, 85, 105] },
        cat2Groups: groupedCart.groups[cat1Key],
      }))

  sections.forEach((section) => {
    const { label, colors, cat2Groups } = section

    // Section header row
    rows.push({
      _type: "cat1Header",
      _cat1Key: label,
      _colors: colors,
      _colSpan: colCount,
    })

    let sectionTotalB2B = 0
    let sectionTotalFinal = 0
    const multipleGroups = Object.keys(cat2Groups).length > 1

    Object.entries(cat2Groups).forEach(([cat2Key, items]) => {
      // Cat2 sub-header (skip for customer if single group in section)
      if (pdfType !== "customer" || multipleGroups) {
        rows.push({
          _type: "cat2Header",
          _cat2Key: cat2Key,
          _colSpan: colCount,
        })
      }

      let cat2SubtotalB2B = 0
      let cat2SubtotalFinal = 0

      items.forEach((item) => {
        itemCounter++
        const b2bPrice = parseFloat(item.b2bsalesprice) || 0
        const finalPrice = calculateFinalPrice(b2bPrice)
        const qty = item.quantity
        const totalB2B = b2bPrice * qty
        const totalFinal = finalPrice * qty
        const profitPerUnit = finalPrice - b2bPrice

        cat2SubtotalB2B += totalB2B
        cat2SubtotalFinal += totalFinal

        let colorText = ""
        if (item.swatch) {
          colorText = item.swatch.label
        }

        const row = {
          _type: "item",
          _sku: item.sku || "",
          idx: itemCounter,
          img: "",
          sku: item.sku || "-",
          product: item.name || "-",
          dimensions: formatCustomDimensions(item.customDimensions),
          color: colorText || "-",
          qty: qty,
        }

        if (pdfType === "manufacturer") {
          row.b2bPrice = `€${b2bPrice.toFixed(2)}`
          row.finalPrice = `€${finalPrice.toFixed(2)}`
          row.profitUnit = `€${profitPerUnit.toFixed(2)}`
          row.total = `€${totalFinal.toFixed(2)}`
        } else if (pdfType === "customer") {
          row.unitPrice = `€${finalPrice.toFixed(2)}`
          row.total = `€${totalFinal.toFixed(2)}`
        } else {
          row.unitPrice = `€${b2bPrice.toFixed(2)}`
          row.total = `€${totalB2B.toFixed(2)}`
        }

        rows.push(row)
      })

      sectionTotalB2B += cat2SubtotalB2B
      sectionTotalFinal += cat2SubtotalFinal

      // Cat2 subtotal (skip for customer if single group)
      if (pdfType !== "customer" || multipleGroups) {
        const subtotalValue = pdfType === "supplier" ? cat2SubtotalB2B : cat2SubtotalFinal
        rows.push({
          _type: "cat2Subtotal",
          _label: cat2Key,
          _value: `€${subtotalValue.toFixed(2)}`,
          _colSpan: colCount,
        })
      }
    })

    grandTotalB2B += sectionTotalB2B
    grandTotalFinal += sectionTotalFinal

    // Section total row
    const sectionValue = pdfType === "supplier" ? sectionTotalB2B : sectionTotalFinal
    rows.push({
      _type: "cat1Total",
      _cat1Key: label,
      _value: `€${sectionValue.toFixed(2)}`,
      _colors: colors,
      _colSpan: colCount,
    })
  })

  // Grand total row
  const grandValue = pdfType === "supplier" ? grandTotalB2B : grandTotalFinal
  rows.push({
    _type: "grandTotal",
    _value: `€${grandValue.toFixed(2)}`,
    _colSpan: colCount,
  })

  return { rows, grandTotalB2B, grandTotalFinal }
}

// ── Manufacturer Summary Box ─────────────────────────────────────────
const drawManufacturerSummary = (doc, { grandTotalB2B, grandTotalFinal, profitMargin, finalY, t }) => {
  const margin = 14
  const boxWidth = 180
  const startY = finalY + 10

  // Check if we need a new page
  const pageHeight = doc.internal.pageSize.getHeight()
  const boxY = startY + 50 > pageHeight ? 20 : startY

  if (startY + 50 > pageHeight) {
    doc.addPage()
  }

  const profit = grandTotalFinal - grandTotalB2B

  // Box border
  doc.setDrawColor(59, 130, 246) // blue
  doc.setLineWidth(0.5)
  doc.roundedRect(margin, boxY, boxWidth, 44, 2, 2)

  // Title
  doc.setFontSize(10)
  doc.setFont("Roboto", "bold")
  doc.setTextColor(59, 130, 246)
  doc.text(t("PROFIT ANALYSIS SUMMARY"), margin + boxWidth / 2, boxY + 8, { align: "center" })

  // Lines
  doc.setFontSize(9)
  doc.setFont("Roboto", "normal")
  doc.setTextColor(71, 85, 105) // #475569

  const lineX = margin + 8
  const valueX = margin + boxWidth - 8

  doc.text(`${t("B2B Total")}:`, lineX, boxY + 17)
  doc.text(`€${grandTotalB2B.toFixed(2)}`, valueX, boxY + 17, { align: "right" })

  doc.text(`${t("Revenue Total")}:`, lineX, boxY + 24)
  doc.text(`€${grandTotalFinal.toFixed(2)}`, valueX, boxY + 24, { align: "right" })

  doc.setFont("Roboto", "bold")
  doc.setTextColor(22, 163, 74) // green
  doc.text(`${t("Total Profit")}:`, lineX, boxY + 31)
  doc.text(`€${profit.toFixed(2)}`, valueX, boxY + 31, { align: "right" })

  doc.setTextColor(124, 58, 237) // purple
  doc.text(`${t("Margin")}:`, lineX, boxY + 38)
  doc.text(`${profitMargin}%`, valueX, boxY + 38, { align: "right" })
}

// ── Page Footers ─────────────────────────────────────────────────────
const addPageFooters = (doc, companyName) => {
  const totalPages = doc.internal.getNumberOfPages()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFontSize(7)
    doc.setFont("Roboto", "normal")
    doc.setTextColor(148, 163, 184) // #94a3b8
    doc.text(
      `${companyName || "FlexFlow"} - Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    )
  }
}

// ── Convert SVG data URI to PNG base64 ───────────────────────────────
const svgDataUriToPng = (svgDataUri) => {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = 100
      canvas.height = 100
      const ctx = canvas.getContext("2d")
      ctx.fillStyle = "#ffffff"
      ctx.fillRect(0, 0, 100, 100)
      ctx.drawImage(img, 0, 0, 100, 100)
      resolve(canvas.toDataURL("image/png"))
    }
    img.onerror = () => resolve(null)
    img.src = svgDataUri
  })
}

// ── Load Product Images as base64 PNG ────────────────────────────────
const loadProductImages = async (cart) => {
  const imageMap = {} // sku → base64 PNG
  const seen = new Set()
  const promises = cart.map(async (item) => {
    const key = item.sku || item.id
    if (!key || seen.has(key)) return
    seen.add(key)

    try {
      // Priority 1: prdimage (inline SVG data URI from DB)
      if (item.prdimage && item.prdimage.startsWith("data:image/svg")) {
        const png = await svgDataUriToPng(item.prdimage)
        if (png) { imageMap[key] = png; return }
      }

      // Priority 2: prdimage as non-SVG data URI (already usable)
      if (item.prdimage && item.prdimage.startsWith("data:image/")) {
        imageMap[key] = item.prdimage
        return
      }

      // Priority 3: productPhotos JSON array of URLs
      if (item.productPhotos) {
        const photos = typeof item.productPhotos === "string"
          ? JSON.parse(item.productPhotos)
          : item.productPhotos
        if (Array.isArray(photos) && photos.length > 0 && photos[0]) {
          const res = await fetch(photos[0])
          const blob = await res.blob()
          const base64 = await new Promise((r) => {
            const reader = new FileReader()
            reader.onloadend = () => r(reader.result)
            reader.onerror = () => r(null)
            reader.readAsDataURL(blob)
          })
          if (base64) imageMap[key] = base64
        }
      }
    } catch { /* skip failed image loads */ }
  })
  await Promise.allSettled(promises)
  return imageMap
}

// ── Main Build PDF Function ──────────────────────────────────────────
const buildPDF = async (params, pdfType) => {
  const { companyDetails, groupedCart, profitMargin, cartItemCount, currentUser, t } = params

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })

  // Load and embed Roboto fonts for Greek support
  await loadFonts(doc)

  const margin = 14

  // Load Eliton logo for customer PDF
  let logoBase64 = null
  let productImages = {}
  if (pdfType === "customer") {
    ;[logoBase64, productImages] = await Promise.all([
      getElitonLogoBase64(),
      loadProductImages(params.cart || []),
    ])
  }

  // Draw header (customer uses Eliton-branded header)
  const tableStartY = pdfType === "customer"
    ? drawCustomerHeader(doc, { ...params, logoBase64 })
    : drawHeader(doc, {
        companyDetails,
        cartItemCount,
        pdfType,
        currentUser,
        t,
      })

  // Build table data
  const columns = getColumns(pdfType, t)
  const { rows, grandTotalB2B, grandTotalFinal } = buildTableBody(params, pdfType)
  const colCount = columns.length

  // Convert structured rows to autotable format
  const tableBody = []

  rows.forEach((row) => {
    if (row._type === "cat1Header") {
      // Full-width colored category header
      const content = [
        {
          content: row._cat1Key,
          colSpan: colCount,
          styles: {
            fillColor: row._colors.bg,
            textColor: row._colors.text,
            fontStyle: "bold",
            fontSize: 9,
            halign: "left",
            cellPadding: { top: 2.5, bottom: 2.5, left: 4, right: 4 },
          },
        },
      ]
      tableBody.push(content)
    } else if (row._type === "cat2Header") {
      // Sub-category header
      const content = [
        {
          content: `    ${row._cat2Key}`,
          colSpan: colCount,
          styles: {
            fillColor: [248, 250, 252], // #f8fafc
            textColor: [71, 85, 105],
            fontStyle: "bold",
            fontSize: 7.5,
            halign: "left",
            cellPadding: { top: 1.5, bottom: 1.5, left: 4, right: 4 },
          },
        },
      ]
      tableBody.push(content)
    } else if (row._type === "cat2Subtotal") {
      const content = [
        {
          content: `${row._label} - Subtotal`,
          colSpan: colCount - 1,
          styles: {
            fillColor: [248, 250, 252],
            textColor: [100, 116, 139],
            fontStyle: "normal",
            fontSize: 7,
            halign: "right",
            cellPadding: { top: 1, bottom: 1, left: 4, right: 2 },
          },
        },
        {
          content: row._value,
          styles: {
            fillColor: [248, 250, 252],
            textColor: [30, 41, 59],
            fontStyle: "bold",
            fontSize: 7.5,
            halign: "right",
            cellPadding: { top: 1, bottom: 1, left: 2, right: 4 },
          },
        },
      ]
      tableBody.push(content)
    } else if (row._type === "cat1Total") {
      const content = [
        {
          content: `${row._cat1Key} - TOTAL`,
          colSpan: colCount - 1,
          styles: {
            fillColor: row._colors.bg,
            textColor: row._colors.text,
            fontStyle: "bold",
            fontSize: 8,
            halign: "right",
            cellPadding: { top: 2, bottom: 2, left: 4, right: 2 },
          },
        },
        {
          content: row._value,
          styles: {
            fillColor: row._colors.bg,
            textColor: row._colors.text,
            fontStyle: "bold",
            fontSize: 9,
            halign: "right",
            cellPadding: { top: 2, bottom: 2, left: 2, right: 4 },
          },
        },
      ]
      tableBody.push(content)
    } else if (row._type === "grandTotal") {
      const content = [
        {
          content: "GRAND TOTAL",
          colSpan: colCount - 1,
          styles: {
            fillColor: [30, 41, 59], // #1e293b
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 9,
            halign: "right",
            cellPadding: { top: 3, bottom: 3, left: 4, right: 2 },
          },
        },
        {
          content: row._value,
          styles: {
            fillColor: [30, 41, 59],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 10,
            halign: "right",
            cellPadding: { top: 3, bottom: 3, left: 2, right: 4 },
          },
        },
      ]
      tableBody.push(content)
    } else {
      // Regular item row — store _sku for image lookup
      const cellValues = columns.map((col) => row[col.dataKey] ?? "")
      cellValues._sku = row._sku
      tableBody.push(cellValues)
    }
  })

  // Column widths
  const pageWidth = doc.internal.pageSize.getWidth() - margin * 2
  let columnStyles = {}

  if (pdfType === "customer") {
    // 9 columns: #, IMG, SKU, Product, Dimensions, Color, Qty, Unit Price, Total
    columnStyles = {
      0: { cellWidth: 7 },   // #
      1: { cellWidth: 12 },  // IMG
      2: { cellWidth: 18 },  // SKU
      3: { cellWidth: "auto" }, // Product (flex)
      4: { cellWidth: 22 },  // Dimensions
      5: { cellWidth: 24 },  // Color
      6: { cellWidth: 10 },  // Qty
      7: { cellWidth: 20 },  // Unit Price
      8: { cellWidth: 20 },  // Total
    }
  } else if (pdfType === "manufacturer") {
    // 10 columns: #, SKU, Product, Dimensions, Color, Qty, B2B, Final, Profit, Total
    columnStyles = {
      0: { cellWidth: 8 },   // #
      1: { cellWidth: 18 },  // SKU
      2: { cellWidth: "auto" }, // Product (flex)
      3: { cellWidth: 22 },  // Dimensions
      4: { cellWidth: 24 },  // Color
      5: { cellWidth: 10 },  // Qty
      6: { cellWidth: 18 },  // B2B
      7: { cellWidth: 18 },  // Final
      8: { cellWidth: 18 },  // Profit
      9: { cellWidth: 18 },  // Total
    }
  } else {
    // 8 columns: #, SKU, Product, Dimensions, Color, Qty, Unit Price, Total
    columnStyles = {
      0: { cellWidth: 8 },   // #
      1: { cellWidth: 20 },  // SKU
      2: { cellWidth: "auto" }, // Product (flex)
      3: { cellWidth: 24 },  // Dimensions
      4: { cellWidth: 28 },  // Color
      5: { cellWidth: 12 },  // Qty
      6: { cellWidth: 22 },  // Unit Price
      7: { cellWidth: 22 },  // Total
    }
  }

  // Draw table
  autoTable(doc, {
    columns: columns.map((col) => ({ header: col.header, dataKey: col.dataKey })),
    body: tableBody,
    startY: tableStartY,
    margin: { left: margin, right: margin },
    styles: {
      font: fontsLoaded ? "Roboto" : "helvetica",
      fontSize: 7.5,
      cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 },
      textColor: [30, 41, 59],
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
    },
    headStyles: pdfType === "customer" ? {
      fillColor: ELITON_PDF.lightRed,
      textColor: ELITON_PDF.redText,
      fontStyle: "bold",
      fontSize: 7,
      cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
      halign: "left",
    } : {
      fillColor: [248, 250, 252],
      textColor: [71, 85, 105],
      fontStyle: "bold",
      fontSize: 7,
      cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
      halign: "left",
    },
    columnStyles,
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    didParseCell: (data) => {
      // Right-align numeric columns
      if (data.section === "body" && data.row.raw && !Array.isArray(data.row.raw)) {
        // This is a special row (header/subtotal/total) - already styled
        return
      }
      if (data.section === "body") {
        const numericCols = pdfType === "manufacturer"
          ? [5, 6, 7, 8, 9]
          : pdfType === "customer"
            ? [6, 7, 8]
            : [5, 6, 7]
        if (numericCols.includes(data.column.index)) {
          data.cell.styles.halign = "right"
        }
        // For customer PDF, ensure image rows have enough height
        if (pdfType === "customer" && Array.isArray(data.row.raw) && data.row.raw._sku) {
          const imgKey = data.row.raw._sku
          if (productImages[imgKey]) {
            data.cell.styles.minCellHeight = 10
          }
        }
      }
    },
    didDrawCell: (data) => {
      // Draw product image in the IMG column (index 1) for customer PDF
      if (pdfType === "customer" && data.section === "body" && data.column.index === 1) {
        if (!Array.isArray(data.row.raw) || !data.row.raw._sku) return
        const imgKey = data.row.raw._sku
        const imgBase64 = productImages[imgKey]
        if (!imgBase64) return

        const cellW = data.cell.width
        const cellH = data.cell.height
        const imgSize = Math.min(cellW - 1, cellH - 1, 9)
        const imgX = data.cell.x + (cellW - imgSize) / 2
        const imgY = data.cell.y + (cellH - imgSize) / 2

        try {
          doc.addImage(imgBase64, "JPEG", imgX, imgY, imgSize, imgSize)
        } catch { /* skip broken images */ }
      }
    },
  })

  // Manufacturer-only: profit summary box
  if (pdfType === "manufacturer") {
    const finalY = doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight() - 60
    drawManufacturerSummary(doc, {
      grandTotalB2B,
      grandTotalFinal,
      profitMargin,
      finalY,
      t,
    })
  }

  // Page footers
  addPageFooters(doc, pdfType === "customer" ? "Eliton" : companyDetails?.name)

  // Open PDF in new browser tab (user can print or download from there)
  const blobUrl = doc.output("bloburl")
  window.open(blobUrl, "_blank")
}

// ── Exported Functions ───────────────────────────────────────────────
export const generateManufacturerPDF = (params) => buildPDF(params, "manufacturer")
export const generateCustomerPDF = (params) => buildPDF(params, "customer")
export const generateSupplierPDF = (params) => buildPDF(params, "supplier")
